import React from 'react'
import { IntlShape } from 'react-intl'

import { PostCartSubmitReservation, PostCartSubmitSuccessResponse } from '../../../types/types'
import { RESERVATION_STATE } from '../../../utils/enums'

export const hasValidStep4SummaryData = (data: PostCartSubmitSuccessResponse) => Boolean(data.reservations[0]?.calendarEvent.salon)

export const getReservationDisplayState = (reservation: PostCartSubmitReservation): RESERVATION_STATE | null => {
	const { state } = reservation

	if (state === RESERVATION_STATE.APPROVED || state === RESERVATION_STATE.PENDING) {
		return state as RESERVATION_STATE
	}

	return null
}

type StatusTooltipContent = {
	title: string
	description: string
}

const formatApprovalDeadline = (awaitingApprovalUntilAt: string, formatDate: IntlShape['formatDate']) =>
	formatDate(awaitingApprovalUntilAt, {
		day: 'numeric',
		month: 'long',
		year: 'numeric',
		hour: 'numeric',
		minute: 'numeric'
	})

export const getReservationStatusTooltipContent = (
	reservation: PostCartSubmitReservation,
	formatMessage: IntlShape['formatMessage'],
	formatDate: IntlShape['formatDate']
): StatusTooltipContent | null => {
	const state = getReservationDisplayState(reservation)

	if (state === RESERVATION_STATE.APPROVED) {
		return {
			title: formatMessage({
				id: 'Confirmed reservation',
				defaultMessage: 'Confirmed reservation'
			}),
			description: formatMessage({
				id: 'Your reservation has been automatically confirmed by the salon, no further action is needed.',
				defaultMessage: 'Your reservation has been automatically confirmed by the salon, no further action is needed.'
			})
		}
	}

	if (state === RESERVATION_STATE.PENDING) {
		const pendingTitle = formatMessage({
			id: 'Reservation awaiting confirmation',
			defaultMessage: 'Reservation awaiting confirmation'
		})

		if (reservation.canBeDelayed) {
			return {
				title: pendingTitle,
				description: formatMessage({
					id: 'Your reservation is awaiting confirmation. It may take longer as the salon staff member is currently on holiday. We will send you confirmation as soon as possible.',
					defaultMessage:
						'Your reservation is awaiting confirmation. It may take longer as the salon staff member is currently on holiday. We will send you confirmation as soon as possible.'
				})
			}
		}

		if (reservation.awaitingApprovalUntilAt) {
			return {
				title: pendingTitle,
				description: formatMessage(
					{
						id: 'The reservation is currently awaiting confirmation, if the salon does not confirm it by { time } it will be automatically cancelled.',
						defaultMessage:
							'The reservation is currently awaiting confirmation, if the salon does not confirm it by { time } it will be automatically cancelled.'
					},
					{
						time: formatApprovalDeadline(reservation.awaitingApprovalUntilAt, formatDate)
					}
				)
			}
		}

		return {
			title: pendingTitle,
			description: formatMessage({
				id: 'Your reservation is currently awaiting confirmation. We will inform you by notification and email when the salon confirms it.',
				defaultMessage: 'Your reservation is currently awaiting confirmation. We will inform you by notification and email when the salon confirms it.'
			})
		}
	}

	return null
}

export const renderReservationStatusTooltipOverlay = (tooltipContent: StatusTooltipContent) => (
	<>
		<strong>{tooltipContent.title}</strong>
		{tooltipContent.description}
	</>
)
